import React, { useState } from 'react';
import 'twin.macro';
import Select from '@teetsh/app/src/components/Form/Select2';
import Input from '@teetsh/app/src/components/Form/Input2';
import { Eleve } from '@teetsh/app/src/types/eleves';
import { StudentTable } from './StudentTable';
import { PotentialDuplicatesTable } from './PotentialDuplicatesTable';
import {
  ClassroomMappingResult,
  MappingAction,
} from './types/ClassroomActions';
import { Classroom } from '@teetsh/app/src/types/classroom';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';
import Disclosure from 'components/Disclosure';
import tw from 'twin.macro';
import { partitionEleves, countEleves } from './partitionEleves';
import { buildImportDetailsMessage } from './buildImportDetailsMessage';

interface Props {
  classroom: ClassroomMappingResult;
  eleves: Eleve[];
  onUpdateClassroom: (
    v: Pick<Classroom, 'id' | 'idBe'> & {
      action: MappingAction;
    },
  ) => void;
  onUpdateEleve: ({
    classroomId,
    eleve,
  }: {
    classroomId: string;
    eleve: Partial<Omit<Eleve, 'customFields>'>> & {
      action: MappingAction;
      customFields?: {
        [key: string]: string;
      };
    };
  }) => void;
}

export function ClassroomMapping(props: Props) {
  const { classroom, eleves, onUpdateEleve, onUpdateClassroom } = props;
  const { t } = useTranslation(['eleve']);
  const classrooms = useClassrooms();

  // even if we set the action to skip (using the checkbox),
  // we still want to display the previous value on the disabled dropdown
  // so we can restore it if we re-enable the classroom
  const [displayedAction, setDisplayAction] = useState(
    classroom.action !== 'skip' ? classroom.action : 'create',
  );
  const partitionedEleves = partitionEleves(classroom, eleves);
  const { newEleves, updatedEleves, existingUnaffectedEleves } =
    partitionedEleves;
  const {
    newElevesCount,
    updatedElevesCount,
    ignoredElevesCount,
    existingUnaffectedElevesCount,
  } = countEleves(partitionedEleves);
  const importDetailsMessage = buildImportDetailsMessage(t, {
    newElevesCount,
    updatedElevesCount,
    ignoredElevesCount,
    existingUnaffectedElevesCount,
  });

  return (
    <div tw="overflow-x-auto">
      <div tw="rounded-lg min-w-[40rem] bg-neutral-supershy p-4 flex flex-col gap-y-4 ">
        <div tw="grid grid-cols-3 gap-4">
          <div tw="flex items-center gap-3">
            <Checkbox
              checked={classroom.action !== 'skip'}
              onChange={e => {
                const newEnabled = e.target.checked;
                if (newEnabled) {
                  onUpdateClassroom({ ...classroom, action: displayedAction });
                } else {
                  onUpdateClassroom({ ...classroom, action: 'skip' });
                }
              }}
              aria-label={`Classe ${classroom.name || 'N/A'}`}
            />
            <div tw="text-neutral-intense">
              {classroom.name || t('eleve:import.na')}
            </div>
          </div>

          <div>
            <Select
              value={displayedAction}
              tw="font-normal bg-white"
              css={[
                classroom.action === 'skip' &&
                  tw`opacity-100! bg-neutral-quiet border-neutral-quiet! text-neutral-supershy! cursor-default! `, // override disabled style to match other disabled inputs
              ]}
              onChange={v => {
                setDisplayAction(v);
                if (classroom.action !== 'skip') {
                  onUpdateClassroom({ ...classroom, action: v });
                }
              }}
              portal
              disabled={classroom.action === 'skip'}
              options={[
                {
                  value: 'create',
                  label: t('eleve:import.createNewClassroom'),
                },
                ...classrooms.map(c => ({ value: c.id, label: c.name })),
              ]}
            />
          </div>

          <div>
            <Input
              value={classroom.idBe || ''}
              onChange={e =>
                onUpdateClassroom({ ...classroom, idBe: e.target.value })
              }
              disabled={classroom.action === 'skip'}
              placeholder="E.g. 0482049304L"
            />
          </div>
        </div>

        <div tw="border border-neutral-quiet rounded-lg">
          <Disclosure defaultOpen={updatedEleves.length > 0} fill>
            <Disclosure.Button
              fill
              large
              alignText="left"
              iconPosition="right"
              iconRotation="bottom-to-top"
              tw="w-full p-4 hover:bg-neutral-quiet transition-colors rounded-none border-0 min-w-0"
            >
              <div tw="flex flex-grow items-center gap-2">
                <div tw="text-sm font-normal text-neutral-intense">
                  <span tw="text-base font-bold">
                    {t('import.studentsList')}
                  </span>
                  {importDetailsMessage}
                </div>
              </div>
            </Disclosure.Button>

            <Disclosure.Panel tw="p-4 space-y-4">
              {/* New Students Section */}
              {newEleves.length > 0 && (
                <div>
                  <div tw="text-sm font-medium text-neutral-intense mb-3">
                    {t('import.newStudents')}
                  </div>
                  <StudentTable
                    students={newEleves}
                    classroomId={classroom.id}
                    onUpdateEleve={onUpdateEleve}
                  />
                </div>
              )}

              {/* Potential Duplicates Section */}
              {updatedEleves.length > 0 && (
                <div>
                  <div tw="text-sm font-medium text-neutral-intense mb-3">
                    {t('import.potentialDuplicatesIdentified')}
                  </div>
                  <PotentialDuplicatesTable
                    students={updatedEleves}
                    classroomId={classroom.id}
                    onUpdateEleve={onUpdateEleve}
                  />
                </div>
              )}

              {/* Existing Students Section */}
              {existingUnaffectedEleves.length > 0 && (
                <div>
                  <div tw="text-sm font-medium text-neutral-intense mb-3">
                    {t('import.existingStudentsUnaffected')}
                  </div>

                  {/* TODO il faudra gérer l'édition dans cette partie là plus tard, ça peut être fait séparément */}
                  {/* là je la tej pour pouvoir finir ma feature pépouze */}
                  {/* en fait ces élèves là ne sont pas dans le state, il faut arriver à les identifier/représenter */}
                  {/* - soit il faut les représenter dans le state (mais il faut les distinguer des autres issues du CSV, sauf que leur action est un uuid) */}
                  {/* - soit il faut qu'ils aient leur state et qu'on les update séparément à la fin quand on finalize */}
                  <StudentTable
                    students={existingUnaffectedEleves}
                    classroomId={classroom.id}
                    onUpdateEleve={onUpdateEleve}
                    enableSkipCheckbox={false}
                    enableEdit={false}
                  />
                </div>
              )}
            </Disclosure.Panel>
          </Disclosure>
        </div>
      </div>
    </div>
  );
}
